import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";

export interface DirectoryGroup {
  id: number;
  organization_id: number;
  workos_directory_id: string | null;
  workos_group_id: string;
  name: string;
  is_deleted: boolean;
}

export interface GroupWorkspaceRoleMapping {
  id: number;
  organization_id: number;
  directory_group_id: number;
  workspace_id: number;
  assigned_role: string;
  created_at: string;
}

export const useDirectoryGroups = (organizationId: number | null) => {
  const userToken = useAuth()?.userToken;
  return useQuery<{ groups: DirectoryGroup[] }>({
    queryKey: ["identity", "directory-groups", organizationId],
    enabled: organizationId !== null && Boolean(userToken),
    queryFn: async () =>
      fetch(ENDPOINTS.identity.groups(organizationId!), {
        headers: { Authorization: `Bearer ${userToken ?? ""}` },
      }).then(handleResponse),
  });
};

export const useGroupMappings = (organizationId: number | null) => {
  const userToken = useAuth()?.userToken;
  return useQuery<{ mappings: GroupWorkspaceRoleMapping[] }>({
    queryKey: ["identity", "group-mappings", organizationId],
    enabled: organizationId !== null && Boolean(userToken),
    queryFn: async () =>
      fetch(ENDPOINTS.identity.group_mappings(organizationId!), {
        headers: { Authorization: `Bearer ${userToken ?? ""}` },
      }).then(handleResponse),
  });
};

export interface CreateGroupMappingBody {
  directory_group_id: number;
  workspace_id: number;
  assigned_role: string;
}

export const useCreateGroupMapping = (organizationId: number) => {
  const userToken = useAuth()?.userToken;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateGroupMappingBody) =>
      fetch(ENDPOINTS.identity.group_mappings(organizationId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken ?? ""}`,
        },
        body: JSON.stringify(body),
      }).then(handleResponse),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["identity", "group-mappings", organizationId],
      });
    },
  });
};

export const useDeleteGroupMapping = (organizationId: number) => {
  const userToken = useAuth()?.userToken;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mappingId: number) =>
      fetch(ENDPOINTS.identity.group_mapping(organizationId, mappingId), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${userToken ?? ""}` },
      }).then(handleResponse),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["identity", "group-mappings", organizationId],
      });
    },
  });
};
